import { Guide } from '../../types/guide';

const guide: Guide = {
  slug: 'split-reorder-flatten-export-pdf-pages',
  title: 'Split, Reorder, Flatten and Export PDF Pages as Images',
  description:
    'Free in-browser PDF page tools: extract page ranges, reorder pages, flatten layered PDFs and export pages as PNG or JPEG images, with no upload.',
  category: 'PDF tools',
  publishedDate: '2026-11-03',
  affiliateCategory: 'scanning',
  body: `
The PDF tools below the [Converter](/) output work on the PDF files themselves. Upload a PDF, click it in the file list to make it the current file, then use the panels below.

## Page ranges: the one syntax to learn

Every page field accepts the same format:

| You type | Pages you get |
|---|---|
| \`5\` | page 5 |
| \`1-3\` | pages 1, 2 and 3 |
| \`1-3, 5, 8-10\` | pages 1, 2, 3, 5, 8, 9, 10 |

Pages are numbered from 1, and the panel shows how many pages the current PDF has.

## Extract (split) pages

Under **Extract / split pages**, enter a range such as \`1-3, 5\` and download a new PDF containing just those pages. Common uses:

- send one chapter or one signed page instead of the whole document,
- split a big scan into separate files by running the tool once per range,
- drop blank or unwanted pages by extracting everything else.

## Reorder pages

Under **Reorder pages**, list **every page exactly once** in the new order. For a 3-page PDF, \`3, 1, 2\` moves the last page to the front. To reverse a 5-page document, enter \`5, 4, 3, 2, 1\`.

If you only want to *remove* pages, use Extract instead — reordering requires every page to appear.

## Merge PDFs

Tick two or more PDFs in the file list and click **Merge selected PDFs**. They're combined in list order. See the full walkthrough in [How to merge PDF files for free](/guides/how-to-merge-pdf-files-for-free).

## Flatten layered PDFs

Some PDFs — maps, technical drawings, forms, design exports — contain **optional content layers**. They can show different content in different viewers, print unexpectedly, or give odd text extraction. Files with layers are marked **"Has layers (can be flattened)"** in the file list.

Turn on **Flatten**, click the **Flatten** button, then download the result. Flattening renders each page exactly as it looks and builds a new PDF from that, so every viewer and printer shows the same thing.

> Flattened pages are images, so the result is ideal for sharing and printing but its text can't be selected afterwards. Keep the original if you still need the text.

## Export pages as images

The **Export Pages as Images** panel turns pages into pictures — great for slides, social posts, thumbnails or pasting a page into a chat.

- **Pages:** leave blank for every page, or use a range like \`1-3, 5\`.
- **Format:** **PNG** for crisp text and diagrams; **JPEG** for photos and smaller files.
- **Resolution:** **Screen (1x)** for quick previews, **High (2x)** for sharp on-screen use, **Print (3x)** for printing or zooming in.

Multiple pages download as individual files, one per page.

## Privacy

All of this happens in your browser with [pdf-lib](https://pdf-lib.js.org/) and Mozilla's [PDF.js](https://mozilla.github.io/pdf.js/). Your PDFs are never uploaded, so it's safe for contracts, statements and other sensitive documents.
`,
};

export default guide;
